import type {ReactNode} from 'react';
import type {TreeResponse} from 'src/api/ProactApiClient';
import type {HierarchyTreeNode} from 'src/design/organisms/HierarchyTree';

/** Per-`kind` icon overrides; nodes without a listed kind fall back to `icon`. */
export type TreeNodeIconsByKind = Record<string, ReactNode>;

export const toHierarchyTreeNodes = (
  nodes: TreeResponse[],
  icon: ReactNode,
  iconsByKind?: TreeNodeIconsByKind
): HierarchyTreeNode[] =>
  nodes.map(node => ({
    id: node.value,
    label: node.label,
    icon: (node.kind && iconsByKind?.[node.kind]) ?? icon,
    count: node.count,
    children:
      node.children.length > 0
        ? toHierarchyTreeNodes(node.children, icon, iconsByKind)
        : undefined,
  }));

/** Tree node id for a numeric record id; `rootId` for missing, zero or invalid ids. */
export const toNumericTreeNodeId = (
  nodeId: string | number | null | undefined,
  rootId: string
): string => {
  const parsed = Number(nodeId);
  return nodeId !== null &&
    nodeId !== undefined &&
    nodeId !== '' &&
    Number.isSafeInteger(parsed) &&
    parsed > 0
    ? String(parsed)
    : rootId;
};

/** Copy of `searchParams` with `node` set to the selected node, or removed for the root. */
export const withNodeSearchParam = (
  searchParams: URLSearchParams,
  nodeId: string,
  rootId: string
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  if (nodeId === rootId) next.delete('node');
  else next.set('node', nodeId);
  return next;
};
